import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'

import { UserRole } from '@brickup/shared/enums'
import { NAV } from '@brickup/shared/locales/de/nav'
import { PROPERTY } from '@brickup/shared/locales/de/property'
import { authUserQuery } from '@/api/auth'
import {
  useProperties,
  usePropertyById,
  useTrashedProperties,
} from '@/api/properties'
import { Button } from '@/components/base'
import {
  listSearchValidator,
  useListFilters,
} from '@/components/Internal/DataTable'
import { MasterDetailLayout } from '@/components/Internal/MasterDetailLayout'
import { PageLayout } from '@/components/Internal/PageLayout'
import { PropertiesTable } from '@/components/Internal/Properties/PropertiesTable'
import { propertyListFilters } from '@/components/Internal/Properties/property-list-filters'
import { PropertyDetails } from '@/components/Internal/Properties/PropertyDetails'
import { PropertyFormModal } from '@/components/Internal/Properties/PropertyFormModal'

export const Route = createFileRoute('/internal/properties')({
  validateSearch: listSearchValidator(propertyListFilters),
  component: PropertiesScreen,
})

/**
 * Every property the robot has found or staff have entered.
 *
 * The bin is the same table over a different source: a deleted property keeps
 * no match rows, so the match filters do not apply there and are left out of
 * the query.
 */
function PropertiesScreen() {
  const filters = useListFilters(propertyListFilters, Route)
  const { trashed, selected } = filters.values

  const [creating, setCreating] = useState(false)

  const { data: authUser } = useQuery(authUserQuery)
  const isRootUser = authUser?.roles.includes(UserRole.ROOT) ?? false

  const queryParams = {
    search: filters.queryParams.search,
    type: filters.queryParams.type,
    sortBy: filters.values.sortBy,
    sortOrder: filters.values.sortOrder,
    ...(trashed
      ? {}
      : {
          customer: filters.queryParams.customer,
          matchStatus: filters.queryParams.matchStatus,
          customerFeedback: filters.queryParams.customerFeedback,
        }),
  }

  const listed = useProperties(queryParams, { enabled: !trashed })
  const binned = useTrashedProperties(queryParams, { enabled: trashed })
  const {
    items: paged,
    total,
    unfilteredTotal,
    isLoading,
    fetchNextPage,
    hasMore,
    isLoadingMore,
  } = trashed ? binned : listed

  // A deep link can name a property outside the loaded page window.
  const { data: linkedProperty } = usePropertyById(selected)

  const properties = useMemo(
    () =>
      linkedProperty && !paged.some(item => item.id === linkedProperty.id)
        ? [linkedProperty, ...paged]
        : paged,
    [paged, linkedProperty],
  )

  const selectedProperty = useMemo(
    () => properties.find(property => property.id === selected) ?? null,
    [properties, selected],
  )

  return (
    <PageLayout title={NAV.properties}>
      <MasterDetailLayout
        storageKey="split:properties"
        sidebar={
          selectedProperty ? (
            <PropertyDetails
              property={selectedProperty}
              onClose={() => filters.set('selected', null)}
            />
          ) : undefined
        }
      >
        <PropertiesTable
          actions={
            <>
              <Button size="sm" icon={Plus} onClick={() => setCreating(true)}>
                {PROPERTY.create}
              </Button>
              <PropertyFormModal
                open={creating}
                onClose={() => setCreating(false)}
              />
            </>
          }
          properties={properties}
          filteredCount={total}
          totalCount={unfilteredTotal ?? total}
          isLoading={isLoading}
          onLoadMore={fetchNextPage}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          isRootUser={isRootUser}
          filters={filters}
        />
      </MasterDetailLayout>
    </PageLayout>
  )
}
